import React, { useEffect, useRef, useState } from "react";
import { ModalType, useModal } from "../../Context/ModalContext";
import { useAppDispatch, useAppSelector } from "../../hook/redux";
import { handleAutoResize } from "../Canvas/createCanvasText";
import { handleDisplayToolbar } from "../utils";

function ZoomEditorModal({canvas}) {
  const {orient} = useAppSelector(state => state.editor.currentData.paramGeneral)
  const appBackground = useAppSelector(state => state.editor.selectedBackground[orient!])
  const { selectedLayout } = useAppSelector(state => state.editor);
  const [ isFocused, setIsFocused ] = useState( false );

  const textareaRef = useRef<HTMLTextAreaElement | null>( null );

  const { show, type, toggleShow } = useModal();

  const dispatch = useAppDispatch();

  let fontSize, fontFamily, fill, text;

  const object = canvas.getActiveObject();
  const data = object && object.toJSON();

  if (data) {
   fontFamily = data.fontFamily
   fill = data.fill
   fontSize = data.fontSize
   text = data.text
  }

  const background = getBackground(appBackground)

  const textSize = Number( fontSize ) * 1.5;

  const alignX = object?.textAlign === "left"
                 ? 'start'
                 : object?.textAlign === "center"
                   ? 'center'
                   : 'end';

  const MODALSTYLE: React.CSSProperties = {
    backgroundColor: `${ background }`,
    padding: "0 !important",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    borderRadius: "30px"
  };

  const TEXTAREASTYLE: React.CSSProperties = {
    background: background,
    width: "100%",
    height: "auto",
    borderRadius: "30px",
    fontFamily: fontFamily,
    color: fill,
    border: isFocused ? "none" : "1px solid transparent",
    outline: "none",
    resize: "none",
    overflow: "hidden",
    fontSize: `${ textSize }px`,
    padding: "10px",
    boxSizing: "border-box",
    textAlign: alignX
  };

  /**
   * Returns the background value as a string based on its type.
   *
   * @param {Object} bg - The background configuration object.
   * @param {string} bg.type - The type of background ("image", "color", "gradient").
   * @param {string} bg.value - The value of the background (e.g., image URL, color code, or gradient configuration).
   * @return {string} The formatted background value based on the type or an empty string if the type is invalid.
   */
  function getBackground(bg: { type: string; value: string; }): string {
    switch (bg.type) {
      case "image":
        return `url(${bg.value})`;
      case "color":
        return bg.value;
      case "gradient":
        return bg.value;
      default:
        return "";
    }
  }


  /**
   * Handles the change event for a textarea element, updates the associated object's text property,
   * resizes elements if necessary, re-renders the canvas, and dispatches an update to the editor state.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e - The change event object from the textarea element.
   * @return {void} This function does not return a value.
   */
  function handleChangeText( e: React.ChangeEvent<HTMLTextAreaElement> ): void {
    const newText = e.target.value;

    if (object?.set) object.set('text', newText);

    handleAutoResize(object)

    dispatch( {
      type: "editor/updateCurrentDataFabricArray",
      payload: {
        layoutId: selectedLayout,
        fabricElements: JSON.stringify(
          canvas.toJSON()
        )
      }
    } );
    adjustHeight();
    canvas.renderAll();

  }

  /**
   * Adjusts the height of a textarea element based on its content.
   * Retrieves the current textarea element using a reference, resets its height to "auto" to recalculate,
   * and then sets the height to match the scrollHeight of the content.
   *
   * This function ensures that the textarea expands or contracts automatically according to its content,
   * providing a dynamic and user-friendly interface.
   *
   * No parameters are accepted, and adjustments are applied directly to the textarea element's style properties.
   */
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if ( textarea ) {
      textarea.style.height = "auto";
      textarea.style.height = `${ textarea.scrollHeight }px`;
    }
  };

  useEffect( () => {
    if ( show ) {
      adjustHeight();
    }
  }, [ show, text ] );

  /**
   * Handles the background click event.
   *
   * This function is triggered when a user clicks on the background of a component. It ensures that the click
   * event is only processed if the click originates from the background itself and not from a child element.
   *
   * @param {React.MouseEvent} e - The mouse event triggered by the user's click.
   *     - `e.target` refers to the element that was clicked.
   *     - `e.currentTarget` refers to the element to which the event handler is attached.
   */
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      toggleShow(false, null, null);
      setTimeout(() => {
        canvas.discardActiveObject();
        canvas.requestRenderAll();

        setTimeout(() => {
          canvas.setActiveObject(object);
          canvas.requestRenderAll();
          handleDisplayToolbar(dispatch, "text", object.idx)
        }, 50);
      }, 150);
    }


  };
  return (
    <div
      className={`custom-modal-background ${show && type === ModalType.zoom ? "" : "hidden"}`}
      onClick={handleBackgroundClick}
    >
      <div
        className={`custom-modal ${show && type === "zoom" ? "" : "hidden"}`}
        aria-labelledby="contained-modal-title-vcenter"
      >
      <div className="custom-modal-body" style={ MODALSTYLE }>
        <textarea
          ref={ textareaRef }
          style={ TEXTAREASTYLE }
          onChange={ ( e ) => handleChangeText( e ) }
          value={ text }
          rows={ 1 }
          onFocus={ () => setIsFocused( true ) }
          onBlur={ () => setIsFocused( false ) }
        >
          { text }
          </textarea>
      </div>
      </div>
    </div>
  );
}

export default ZoomEditorModal;